import { useEffect, useState } from "react";
import { MessageCircle, Plus, X, Leaf } from "lucide-react";
import { fetchOrders, createOrder } from "../../services/orderApi";
import { fetchProducts } from "../../services/productApi";
import { toast } from "../../hooks/use-toast";
import api from "../../services/api";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddOrderForm, setShowAddOrderForm] = useState(false);
  const [orderForm, setOrderForm] = useState({
    customerName: "",
    whatsappNumber: "",
    items: [{ productId: "", productName: "", price: "", quantity: 1 }],
  });

  const selectedIds = orderForm.items
    .map((i) => i.productId)
    .filter(Boolean);

  // Load pruducts once
  useEffect(() => {
    fetchProducts().then(setProducts);
  }, []);

  useEffect(() => {
    try {
      fetchOrders().then(setOrders);
    } catch (err) {
      setError("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, []);

  const addItem = () => {
    setOrderForm({
      ...orderForm,
      items: [
        ...orderForm.items,
        { productId: "", productName: "", price: "", quantity: 1 },
      ],
    });
  };

  const updateItem = (index, field, value) => {
    const items = [...orderForm.items];
    items[index][field] = value;
    setOrderForm({ ...orderForm, items });
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();

    try {
      await createOrder({
        customerName: orderForm.customerName,
        whatsappNumber: orderForm.whatsappNumber,
        items: orderForm.items.map((i) => ({
          productName: i.productName,
          price: Number(i.price),
          quantity: Number(i.quantity),
        })),
      });

      toast({
        title: "Order Created",
        description: "WhatsApp order has been logged successfully.",
      });

      setShowAddOrderForm(false);
      setOrderForm({
        customerName: "",
        whatsappNumber: "",
        items: [{ productId: "", productName: "", price: "", quantity: 1 }],
      });

      const data = await fetchOrders();
      setOrders(data);
    } catch {
      toast({
        title: "Error",
        description: "Failed to create order",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.post(`/orders/edit/${id}/status`,
        {
          status
        }
      );

      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to update order status",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "bg-herb-green/10 text-herb-green";
      case "shipped":
        return "bg-blue-500/10 text-blue-500";
      case "confirmed":
        return "bg-amber-500/10 text-amber-500";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-serif font-bold text-foreground mb-2">
          WhatsApp Orders
        </h2>
        <p className="text-muted-foreground">
          Track and manage orders received via WhatsApp
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-whatsapp/10 border border-whatsapp/20 rounded-2xl p-6 flex items-start gap-4">
        <div className="w-12 h-12 bg-whatsapp/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-6 h-6 text-whatsapp" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground mb-1">
            WhatsApp Only Orders
          </h3>
          <p className="text-muted-foreground text-sm">
            All orders are received and processed through WhatsApp. This table
            displays order information logged from WhatsApp conversations.
          </p>
        </div>
      </div>
      {/* Add Order Button */}
      <button
        onClick={() => setShowAddOrderForm(true)}
        className="btn-hero flex items-center gap-2 px-5 py-3 text-sm"
      >
        <Plus size={18} />
        <span>Add Order</span>
      </button>

      {showAddOrderForm && (
        <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-large max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-xl font-serif font-semibold">
                Confirm WhatsApp Order
              </h3>
              <button
                onClick={() => setShowAddOrderForm(false)}
                className="p-2 hover:bg-muted rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateOrder} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Customer Name
                  </label>
                  <input
                    required
                    value={orderForm.customerName}
                    onChange={(e) =>
                      setOrderForm({
                        ...orderForm,
                        customerName: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    WhatsApp Number
                  </label>
                  <input
                    required
                    value={orderForm.whatsappNumber}
                    onChange={(e) =>
                      setOrderForm({
                        ...orderForm,
                        whatsappNumber: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">Products</h4>

                {orderForm.items.map((item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 md:grid-cols-3 gap-4"
                  >
                    <select
                      required
                      value={item.productId}
                      onChange={(e) => {
                        const selected = products.find(
                          (p) => p.id === Number(e.target.value)
                        );
                        updateItem(index, "productId", selected.id);
                        updateItem(index, "productName", selected.name);
                        // OPTIONAL: auto-fill price
                        updateItem(index, "price", selected.price);
                      }}
                      className="px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                    >
                      <option value="">Select product</option>
                      {products.map((product) => (
                        <option
                          key={product.id}
                          value={product.id}
                          disabled={
                            selectedIds.includes(product.id) &&
                            product.id !== item.productId
                          }
                        >
                          {product.name} ({product.category?.name})
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      required
                      value={item.price}
                      onChange={(e) =>
                        updateItem(index, "price", e.target.value)
                      }
                      className="px-4 py-3 rounded-xl bg-background border border-border"
                      disabled={!item.productId}
                    />
                    <input
                      type="number"
                      min="1"
                      placeholder="Qty"
                      required
                      onChange={(e) =>
                        updateItem(index, "quantity", e.target.value)
                      }
                      className="px-4 py-3 rounded-xl bg-background border border-border"
                    />
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addItem}
                  className="text-sm text-primary hover:underline"
                >
                  + Add another product
                </button>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowAddOrderForm(false)}
                  className="flex-1 px-4 py-3 rounded-xl border border-border hover:bg-muted"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-hero">
                  Confirm Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-card rounded-2xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                  Order ID
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                  Customer Name
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                  Product
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                  WhatsApp
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                  Qty
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                  Total
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center py-6">
                    Loading orders...
                  </td>
                </tr>
              )}
              {error && (
                <tr>
                  <td colSpan={8} className="text-center text-red-500 py-6">
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && orders.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-16">
                    <Leaf className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
                    <p className="text-lg text-muted-foreground">
                      No orders available at the moment.
                    </p>
                  </td>
                </tr>
              )}
              {!loading && !error &&
                orders.map((order) => (
                  <tr
                    key={order.id}
                    className="hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-foreground">
                      {order.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground">
                      {order.customerName}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <ul className="space-y-1">
                        {order.items.map((item, i) => (
                          <li key={i}>
                            {item.productName} × {item.quantity}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {order.whatsappNumber}
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground">
                      {order.items.reduce((sum, i) => sum + i.quantity, 0)}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-foreground">
                      ${Number(order.totalAmount || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={order.status}
                        onChange={(e) => updateStatus(order.id, e.target.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium ${getStatusColor(
                          order.status
                        )}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-muted/50 p-4 rounded-xl text-center">
          <p className="text-2xl font-bold text-foreground">
            {orders.filter((o) => o.status === "pending").length}
          </p>
          <p className="text-sm text-muted-foreground">Pending</p>
        </div>
        <div className="bg-amber-500/10 p-4 rounded-xl text-center">
          <p className="text-2xl font-bold text-amber-600">
            {orders.filter((o) => o.status === "confirmed").length}
          </p>
          <p className="text-sm text-amber-600/70">Confirmed</p>
        </div>
        <div className="bg-blue-500/10 p-4 rounded-xl text-center">
          <p className="text-2xl font-bold text-blue-600">
            {orders.filter((o) => o.status === "shipped").length}
          </p>
          <p className="text-sm text-blue-600/70">Shipped</p>
        </div>
        <div className="bg-herb-green/10 p-4 rounded-xl text-center">
          <p className="text-2xl font-bold text-herb-green">
            {orders.filter((o) => o.status === "delivered").length}
          </p>
          <p className="text-sm text-herb-green/70">Delivered</p>
        </div>
      </div>
    </div>
  );
};

export default AdminOrders;
